import cx from "classnames";
import { QuestionAnswer, ValidationResult } from "../../types/forms";
import ValidationWarning from "./ValidationWarning";

interface TextInputProps {
  /** The onChange event, for handling state changes */
  onChange(newValue: string): void;
  /** An event to call when the input loses focus */
  onBlur?(
    latestValue: string,
    nonStateValue?: QuestionAnswer | null
  ): void | undefined;
  /** The current value for the input */
  value?: string;
  /** The Id attribute for the input element, to match the Label */
  inputId?: string;
  /** The css class names to apply */
  className?: string;
  /** The Placeholder attribute value for the form element */
  placeholder?: string | undefined;
  /** A ref for calling methods on the input element, e.g. to focus it */
  inputRef?: React.RefObject<HTMLInputElement> | undefined;
  /** Whether or not to display the validation warnings */
  showValidationErrors?: boolean;
  /** If validation has been run, this is the validity plus any errors */
  validationResult?: ValidationResult | null;
  isReadOnly?: boolean;
  maxLength?: number;
  /** Native input type (defaults to text) */
  type?: "text" | "email" | "password" | "search" | "tel" | "url";
  /** Whether to prevent password managers from suggesting auto-fill for this field */
  preventPasswordManagerPrompt?: boolean;
  /** Optional id of a hint/description element for screen readers */
  ariaDescribedBy?: string;
  /** Marks the field invalid for assistive tech */
  ariaInvalid?: boolean;
}

/** A text input field, with common styling already applied
 */
const TextInput = ({
  onChange,
  onBlur = undefined,
  showValidationErrors = false,
  validationResult = null,
  value = "",
  inputId = "",
  className = "block w-full border-0 border-none",
  placeholder = undefined,
  inputRef = undefined,
  isReadOnly = false,
  maxLength = undefined,
  type = "text",
  preventPasswordManagerPrompt = false,
  ariaDescribedBy = undefined,
  ariaInvalid = undefined,
}: TextInputProps) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (onBlur) {
      onBlur(e.target.value);
    }
  };

  return (
    <div>
      {showValidationErrors && validationResult && (
        <ValidationWarning
          isValid={validationResult.isValid}
          errors={validationResult.errors}
        />
      )}
      <input
        type={type}
        id={inputId}
        onChange={handleChange}
        onBlur={handleBlur}
        value={value ? value : ""}
        className={cx(
          className,
          "disabled:cursor-not-allowed rounded-md focus:outline-none focus:ring-0 disabled:text-gray-600"
        )}
        placeholder={placeholder}
        ref={inputRef}
        disabled={isReadOnly}
        maxLength={maxLength}
        aria-describedby={ariaDescribedBy}
        aria-invalid={ariaInvalid}
        {...(preventPasswordManagerPrompt
          ? {
              autoComplete: "off",
              autoCorrect: "off",
              autoCapitalize: "off",
              spellCheck: "false",
              "data-form-type": "other",
              "data-1p-ignore": "true",
            }
          : {})}
      />
    </div>
  );
};

export default TextInput;
